import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import type { ActivityEvent, ActivityFamily } from '@/types';
import { FAMILY_META } from '@/features/trades/utils/activity-family';

export type ActivityFilterValue = ActivityFamily | 'ALL';

interface ActivityFilterProps {
  events: ActivityEvent[];
  value: ActivityFilterValue;
  onChange: (value: ActivityFilterValue) => void;
}

/** Stable family render order. */
const FAMILY_ORDER: ActivityFamily[] = ['lifecycle', 'valuation', 'accounting', 'corporate'];

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'rounded-md border px-3 py-1 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent',
        active
          ? 'border-lr-fg bg-lr-fg text-white'
          : 'border-lr-border bg-lr-surface text-lr-muted hover:text-lr-fg-2',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Header family filter as button pills (prototype `.filters`) over the
 * already-fetched stream: "All families ({total})" + one pill per family
 * PRESENT in the data (count > 0). Families are the backend taxonomy
 * (lifecycle / valuation / accounting / corporate) — settlement events are
 * lifecycle, not a separate family.
 */
export function ActivityFilter({ events, value, onChange }: ActivityFilterProps) {
  const counts = useMemo(() => {
    const map: Record<ActivityFamily, number> = {
      lifecycle: 0,
      valuation: 0,
      accounting: 0,
      corporate: 0,
    };
    for (const e of events) map[e.family] += 1;
    return map;
  }, [events]);

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter activity by family">
      <Pill active={value === 'ALL'} onClick={() => onChange('ALL')}>
        All families <span className="lr-num">({events.length})</span>
      </Pill>
      {FAMILY_ORDER.map((family) => {
        const count = counts[family];
        if (count === 0) return null;
        return (
          <Pill key={family} active={value === family} onClick={() => onChange(family)}>
            {FAMILY_META[family].label}
          </Pill>
        );
      })}
    </div>
  );
}
